import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ShieldCheckIcon,
  FlagIcon,
  TrophyIcon,
  UserGroupIcon,
  SparklesIcon,
  FunnelIcon,
  HandThumbUpIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import authService from '../../services/AuthServiceWrapper';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const Title = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  background: ${props => props.theme.colors.gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.5px;
`;

const ContentArea = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Intro = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};
  text-align: center;
`;

const IntroTitle = styled.h2`
  font-size: 1.5rem;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const IntroText = styled.p`
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.6;
  font-size: ${props => props.theme.typography.body.fontSize};
`;

const SectionGrid = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing.lg};
`;

const Section = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.lg};
  background: ${props => props.theme.colors.background};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const SectionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.lg};
  background: ${props => props.color || props.theme.colors.accent}20;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 20px;
    height: 20px;
    color: ${props => props.color || props.theme.colors.accent};
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
`;

const FAQList = styled.div`
  padding: ${props => props.theme.spacing.lg};
`;

const FAQItem = styled.div`
  border-bottom: 1px solid ${props => props.theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const FAQQuestion = styled.button`
  width: 100%;
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.primary};
  text-align: left;
  padding: ${props => props.theme.spacing.md} 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${props => props.theme.typography.body.fontSize};
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${props => props.theme.colors.accent};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const FAQAnswer = styled(motion.div)`
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.6;
  font-size: ${props => props.theme.typography.caption.fontSize};
  padding-bottom: ${props => props.theme.spacing.md};
  
  strong {
    color: ${props => props.theme.colors.text.primary};
  }
  
  em {
    color: ${props => props.theme.colors.accent};
  }
  
  ul {
    margin: ${props => props.theme.spacing.sm} 0;
    padding-left: ${props => props.theme.spacing.lg};
  }
  
  li {
    margin-bottom: ${props => props.theme.spacing.xs};
  }
`;

const InlineIcon = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  
  svg {
    width: 16px;
    height: 16px;
    color: ${props => props.color || props.theme.colors.accent};
  }
`;

const LevelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing.md};
  margin: ${props => props.theme.spacing.md} 0;
`;

const LevelCard = styled.div`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  text-align: center;
`;

const LevelIcon = styled.div`
  font-size: 2rem;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const LevelName = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const LevelPoints = styled.div`
  font-size: ${props => props.theme.typography.small.fontSize};
  color: ${props => props.theme.colors.text.secondary};
`;

const FAQ = () => {
  const navigate = useNavigate();
  const [openSections, setOpenSections] = useState(new Set(['getting-started']));

  useEffect(() => {
    // Subscribe to auth changes to maintain auth state during navigation
    const unsubscribeAuth = authService.addListener(() => {
      // Auth state managed by service
    });

    return () => {
      authService.removeListener(unsubscribeAuth);
    };
  }, []);

  const toggleSection = (sectionId) => {
    const newOpenSections = new Set(openSections);
    if (newOpenSections.has(sectionId)) {
      newOpenSections.delete(sectionId);
    } else {
      newOpenSections.add(sectionId);
    }
    setOpenSections(newOpenSections);
  };

  const faqSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: SparklesIcon,
      color: '#8B5CF6',
      questions: [
        {
          q: 'What is SceneFeed?',
          a: 'SceneFeed is a community-driven platform for discovering and sharing live music shows in your area. Users can post shows, discover events, and help moderate content to keep the community spam-free.'
        },
        {
          q: 'How do I add a show?',
          a: 'Click the purple "+" button on the home page (when logged in) or use the hamburger menu to navigate to "Add Show". Fill out the form with show details and submit for community approval.'
        },
        {
          q: 'Why do I need to create an account?',
          a: 'An account lets you post shows, like events, report inappropriate content, participate in moderation, and earn scene points. It helps us maintain a quality community experience.'
        },
        {
          q: 'How do I save shows I want to attend?',
          a: 'heart-content' // Special marker for JSX content
        },
        {
          q: 'What are the different account types?',
          a: `<strong>Fan:</strong> Music lovers who attend shows and discover new events<br/>
          <strong>Band/Artist:</strong> Musicians who can post their own shows with auto-approval<br/>
          <strong>Venue:</strong> Concert venues that can post shows with special privileges`
        }
      ]
    },
    {
      id: 'scene-points',
      title: 'Scene Points System',
      icon: TrophyIcon,
      color: '#10B981',
      questions: [
        {
          q: 'What are scene points?',
          a: 'Scene points measure your contribution to the community. You earn points by posting shows, moderating content, and having your submissions approved. Higher points give you more moderation power and community status.'
        },
        {
          q: 'How do I earn scene points?',
          a: `You earn points through several activities:<br/>
          <strong>• Posting approved shows:</strong> 5-10 points per show<br/>
          <strong>• Moderating content:</strong> 1-3 points per approval, 1-2 points per removal (based on your level)<br/>
          <strong>• Receiving thank yous:</strong> 2 points when other users thank you for posting shows<br/>
          <strong>• Quality contributions:</strong> Bonus points for well-detailed, popular shows<br/>
          <strong>• Community involvement:</strong> Regular participation and helpful behavior`
        },
        {
          q: 'How does the thank you system work?',
          a: 'thank-you-content' // Special marker for JSX content
        },
        {
          q: 'What are the different user levels?',
          a: 'levels-grid' // Special marker for custom content
        },
        {
          q: 'Do scene points expire?',
          a: 'No! Scene points are permanent and represent your lifetime contribution to the community. Your level badge will always reflect your highest achievement.'
        }
      ]
    },
    {
      id: 'moderation',
      title: 'Moderation System',
      icon: ShieldCheckIcon,
      color: '#3B82F6',
      questions: [
        {
          q: 'How does show approval work?',
          a: `All new shows need community approval before going live:<br/>
          <strong>• Fan submissions:</strong> Need 3 approvals from trusted users<br/>
          <strong>• Band/Venue submissions:</strong> Auto-approved (they know their own shows!)<br/>
          <strong>• Reported shows:</strong> Need 1 moderator review<br/>
          <strong>• Higher-level users:</strong> Have more approval power`
        },
        {
          q: 'Who can approve shows?',
          a: `Any logged-in user can participate in moderation, but approval power varies:<br/>
          <strong>• Scene Legends (1000+ pts):</strong> 3 approval power<br/>
          <strong>• Scene Veterans (500+ pts):</strong> 2 approval power<br/>
          <strong>• All other users:</strong> 1 approval power<br/>
          <em>Note: You cannot approve your own submissions</em>`
        },
        {
          q: 'What happens to rejected shows?',
          a: 'Rejected shows are removed from the approval queue and do not appear on the site. The poster may receive feedback and can resubmit an improved version.'
        },
        {
          q: 'How do I access the moderation dashboard?',
          a: 'Click the hamburger menu (☰) and select "Moderation" to review pending shows. You will see submission details, approval progress, and can approve or reject content.'
        }
      ]
    },
    {
      id: 'reporting',
      title: 'Reporting System',
      icon: FlagIcon,
      color: '#EF4444',
      questions: [
        {
          q: 'When should I report a show?',
          a: `Report shows that violate community guidelines:<br/>
          <strong>• Spam or fake events:</strong> Promotional spam, fake shows, duplicates<br/>
          <strong>• Inappropriate content:</strong> Offensive language, discriminatory content<br/>
          &nbsp;&nbsp;<em>This means NO Racism, NO Sexism, NO Homophobia, NO Transphobia.<br/>
          &nbsp;&nbsp;Shows are for FUN NOT HATE!</em><br/>
          <strong>• Wrong information:</strong> Incorrect details, misleading descriptions<br/>
          <strong>• Scams:</strong> Suspicious ticketing, fake venues, fraudulent activity`
        },
        {
          q: 'What happens when I report a show?',
          a: 'Reported shows go to the moderation queue for review. Reports from <strong>Scene Insiders, Veterans, and Legends count as 3 reports each</strong> due to their trusted status. If a show reaches <strong>5 total reports</strong>, it is automatically hidden until moderators can review it. Your message and reason are always visible to moderators. For <strong>Inappropriate Content, Harassment, and Other reports</strong>, your identity is protected and shown as "Anonymous Reporter" to prevent retaliation.'
        },
        {
          q: 'Can I see who reported content?',
          a: 'Report privacy depends on the category: <strong>Spam, Wrong Information, and Scam reports</strong> show the reporter username for accountability. <strong>Inappropriate Content, Harassment, and Other reports</strong> are completely anonymous to protect reporters from potential retaliation. All report details and categories are visible to moderators.'
        },
        {
          q: 'What if I accidentally report something?',
          a: 'Don\'t worry! One accidental report won\'t cause issues. Shows need multiple reports to be automatically hidden, and moderators review all reports carefully.'
        }
      ]
    },
    {
      id: 'filters',
      title: 'Searching & Filters',
      icon: FunnelIcon,
      color: '#8B5CF6',
      questions: [
        {
          q: 'How do I find shows near me?',
          a: `SceneFeed uses your selected location to find shows. Use the location picker in the header to change your city. Shows are automatically filtered by your location and selected radius.`
        },
        {
          q: 'What is the radius filter?',
          a: `The radius filter lets you discover shows in nearby cities within a certain distance from your location:<br/>
          <strong>• 10 miles:</strong> Shows in your immediate area<br/>
          <strong>• 25 miles:</strong> Default setting, includes nearby cities<br/>
          <strong>• 50 miles:</strong> Regional shows within driving distance<br/>
          <strong>• 100 miles:</strong> Extended area for major events<br/>
          <em>Shows from nearby cities will display the distance from your location.</em>`
        },
        {
          q: 'How do I search for specific bands or venues?',
          a: 'Use the search bar at the top of the filters section. You can search by band names, venue names, or even music genres. The search works across all show details including descriptions.'
        },
        {
          q: 'Can I filter by date or event type?',
          a: `Yes! Click the "Filters" button to access advanced options:<br/>
          <strong>• Date filter:</strong> Find shows on specific dates<br/>
          <strong>• Venue filter:</strong> Shows at specific venues<br/>
          <strong>• Posted by:</strong> Filter by who posted (fans, bands, venues)<br/>
          <strong>• Radius:</strong> Adjust distance range for nearby cities`
        },
        {
          q: 'Why don\'t I see shows in my city?',
          a: 'Make sure your location is set correctly using the location picker. If there are no shows in your immediate area, try increasing your radius to include nearby cities. You can also help grow your local scene by posting shows!'
        }
      ]
    },
    {
      id: 'community',
      title: 'Community Guidelines',
      icon: UserGroupIcon,
      color: '#F59E0B',
      questions: [
        {
          q: 'What makes a good show submission?',
          a: `High-quality submissions include:<br/>
          <strong>• Accurate details:</strong> Correct date, time, venue, and band information<br/>
          <strong>• Clear descriptions:</strong> What to expect, age limits, ticket info<br/>
          <strong>• Proper categorization:</strong> Correct genre and event type<br/>
          <strong>• Helpful context:</strong> Directions, parking, special instructions`
        },
        {
          q: 'What content is not allowed?',
          a: `We don't allow:<br/>
          • Fake or spam events<br/>
          • Discriminatory or offensive content<br/>
          &nbsp;&nbsp;<em>NO Racism, NO Sexism, NO Homophobia, NO Transphobia</em><br/>
          • Commercial spam unrelated to live music<br/>
          • Deliberately false information<br/>
          • Personal attacks or harassment<br/>
          <strong>Shows are for FUN NOT HATE!</strong>`
        },
        {
          q: 'How can I help improve the community?',
          a: 'Participate in moderation, report inappropriate content, submit detailed show information, help newcomers, and be respectful in all interactions. Quality contributions earn you scene points and help everyone discover great music!'
        },
        {
          q: 'What if I have a problem with another user?',
          a: 'Use the report system for inappropriate content. For serious issues, you can contact moderators through the community. We take harassment and abuse seriously.'
        }
      ]
    }
  ];

  const userLevels = [
    { icon: '🤡', name: 'POSER', points: '0-19 pts' },
    { icon: '📻', name: 'Scene Supporter', points: '20-49 pts' },
    { icon: '🎧', name: 'Active Member', points: '50-99 pts' },
    { icon: '🎵', name: 'Trusted Poster', points: '100-249 pts' },
    { icon: '🎤', name: 'Scene Insider', points: '250-499 pts' },
    { icon: '🎸', name: 'Scene Veteran', points: '500-999 pts' },
    { icon: '👑', name: 'Scene Legend', points: '1000+ pts' }
  ];

  return (
    <Container>
      <Header>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#8B5CF6', fontSize: '1.2em', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, marginBottom: '8px', cursor: 'pointer' }}>
          <ArrowLeftIcon style={{ width: 24, height: 24 }} />
          Back
        </button>
        <Title>FAQ & Help</Title>
      </Header>

      <ContentArea>
        <Intro>
          <IntroTitle>Welcome to the SceneFeed Community! 🎵</IntroTitle>
          <IntroText>
            Here's everything you need to know about discovering shows, earning scene points, 
            participating in moderation, and helping build the best live music community.
          </IntroText>
        </Intro>

        <SectionGrid>
          {faqSections.map((section) => (
            <Section key={section.id}>
              <SectionHeader>
                <SectionIcon color={section.color}>
                  <section.icon />
                </SectionIcon>
                <SectionTitle>{section.title}</SectionTitle>
              </SectionHeader>

              <FAQList>
                {section.questions.map((faq, index) => (
                  <FAQItem key={index}>
                    <FAQQuestion onClick={() => toggleSection(`${section.id}-${index}`)}>
                      {faq.q}
                      {openSections.has(`${section.id}-${index}`) ? 
                        <ChevronUpIcon /> : 
                        <ChevronDownIcon />
                      }
                    </FAQQuestion>
                    
                    <AnimatePresence>
                      {openSections.has(`${section.id}-${index}`) && (
                        <FAQAnswer
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {faq.a === 'levels-grid' ? (
                            <LevelGrid>
                              {userLevels.map((level) => (
                                <LevelCard key={level.name}>
                                  <LevelIcon>{level.icon}</LevelIcon>
                                  <LevelName>{level.name}</LevelName>
                                  <LevelPoints>{level.points}</LevelPoints>
                                </LevelCard>
                              ))}
                            </LevelGrid>
                          ) : faq.a === 'thank-you-content' ? (
                            <div>
                              Users can thank fan posters for submitting shows by clicking the <InlineIcon color="#10B981"><HandThumbUpIcon /> thumbs up</InlineIcon> button on show cards:<br/>
                              <strong>• Award points:</strong> Each thank you gives the poster 2 scene points<br/>
                              <strong>• One per user:</strong> You can only thank each show once<br/>
                              <strong>• Fan posts only:</strong> Only shows posted by fans can be thanked (bands/venues auto-approve)<br/>
                              <strong>• Encourages quality:</strong> Rewards users for posting detailed, helpful show information<br/>
                              <em>This helps recognize community members who contribute valuable show information!</em>
                            </div>
                          ) : faq.a === 'heart-content' ? (
                            <div>
                              Click the <InlineIcon color="#8B5CF6"><HeartIcon /> heart</InlineIcon> button on any show card to save it to your favorites. Saved shows appear in <strong>My Shows</strong> (accessible from the hamburger menu) so you can easily find events you want to attend. The heart turns purple when you've saved a show, making it easy to see which events you've saved at a glance.
                            </div>
                          ) : (
                            <div dangerouslySetInnerHTML={{ 
                              __html: faq.a.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                           .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                           .replace(/\n/g, '<br/>')
                            }} />
                          )}
                        </FAQAnswer>
                      )}
                    </AnimatePresence>
                  </FAQItem>
                ))}
              </FAQList>
            </Section>
          ))}
        </SectionGrid>
      </ContentArea>
    </Container>
  );
};

export default FAQ; 